"use client"
import PostCardItem from "@/(admin)/modules/ui-builder/config/blocks/PostGrid/PostCardItem"
import { t } from "@/languages/i18n"
import { getSiteIdFromHost } from "@/sites/config"
import { ComponentConfig } from "@measured/puck"
import { useEffect, useState } from "react"
import { fetchPostByCategories } from "../../../../../../services/postServices"

export type PostGridByCategoryProps = {
  idCategory: string[]
}

export const RenderConfig: ComponentConfig<PostGridByCategoryProps> = {
  render: ({ idCategory }) => {
    const [currentPagePosts, setCurrentPagePosts] = useState<any[]>([])
    const [totalPosts, setTotalPosts] = useState(0)
    const [currentPage, setCurrentPage] = useState(1)
    const [postsPerPage] = useState(9)
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const host = window.location.host
    const siteId = getSiteIdFromHost(host)

    const fetchPosts = async (categoryIds: string[], page: number = 1, append: boolean = false) => {
      if (categoryIds.length === 0) {
        setCurrentPagePosts([])
        setTotalPosts(0)
        return
      }

      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }

      try {
        const paginationParams = {
          page: page.toString(),
          pageSize: postsPerPage.toString(),
          siteId: siteId
        }

        const response = await fetchPostByCategories(categoryIds, paginationParams)
        if (response) {
          if (append && page > 1) {
            setCurrentPagePosts(prev => [...prev, ...response.data])
          } else {
            setCurrentPagePosts(response.data)
          }
          setTotalPosts(response.meta?.pagination?.total || response.data.length)
        } else {
          if (!append) {
            setCurrentPagePosts([])
            setTotalPosts(0)
          }
        }
      } catch (err) {
        console.error("Failed to fetch posts. Please try again.")
      } finally {
        if (append) {
          setLoadingMore(false)
        } else {
          setLoading(false)
        }
      }
    }

    useEffect(() => {
      setCurrentPagePosts([])
      setTotalPosts(0)
      setCurrentPage(1)
      fetchPosts(idCategory, 1)
    }, [idCategory, siteId])

    const totalPages = Math.ceil(totalPosts / postsPerPage)

    const handlePageChange = async (page: number) => {
      setCurrentPage(page)
      await fetchPosts(idCategory, page, true)
    }

    return (
      <>
        {(currentPagePosts.length > 0 || loading) && (
          <div className="flex flex-col sm:flex-row justify-center sm:gap-[45px] 3xl:gap-[145px] gap-[30px] mx-[20px] sm:mx-[30px]">
            <div className="w-full sm:w-[1200px] flex flex-col sm:gap-10 gap-8 sm:pt-10 sm:pb-25 py-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-x-10 sm:gap-y-16 w-full">
                {loading ? (
                  Array.from({ length: postsPerPage }).map((_, index) => (
                    <div key={`loading-${index}`} className="flex-shrink-0 w-full md:w-[373.33px]">
                      <div className="flex flex-col gap-4">
                        <div className="relative h-[223.17px] w-full overflow-hidden rounded-3xl bg-gray-200 animate-pulse"></div>

                        <div className="flex flex-col gap-1.5">
                          <div className="flex flex-col">
                            <div className="flex items-center mb-1.5">
                              <div className="h-[5px] w-[5px] bg-gray-300 rounded-full mr-2"></div>
                              <div className="h-4 bg-gray-200 rounded animate-pulse w-24"></div>
                            </div>
                            <div className="h-5 bg-gray-200 rounded animate-pulse w-full mb-1"></div>
                            <div className="h-5 bg-gray-200 rounded animate-pulse w-3/4"></div>
                          </div>
                          <div className="space-y-1">
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-full"></div>
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-5/6"></div>
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-4/5"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    {currentPagePosts?.map((item, index) => (
                      <PostCardItem key={index} post={item} />
                    ))}
                  </>
                )}
              </div>
              {currentPage < totalPages && (
                <div className="col-span-full flex justify-center">
                  <button
                    className="sm:px-8 sm:py-3 px-6 py-2 bg-[#1E4259] border border-[#1E4259] sm:text-base sm:leading-[22px] text-sm font-semibold text-white rounded-full hover:bg-white hover:text-[#1E4259] transition duration-300"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={loadingMore}
                  >
                    {loadingMore ? t("Loading...") : t("Load More")}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </>
    )
  },
}
